import { useState, useEffect, useMemo, useCallback } from "react";
import BaseModal from "../../../BaseComponents/BaseModal/BaseModal";
import SearchableSelect from "../../../BaseComponents/SearchableSelect/SearchableSelect";
import { useGetAirlines } from "../../../../api/products/useAirlinesApi";
import styles from "./AddFareClassModal.module.css";

const FLIGHT_SCOPES = ["Domestic", "International", "Intercontinental", "Touristic", "Special", "All Flights"];
const FARE_TYPES = ["Light", "Standard", "Flex", "Premium", "Touristic", "Basic", "Plus"];
const CHECKIN_STATUSES = [
  { value: "", label: "Not configured (use airline/system default)" },
  { value: "required", label: "Required" },
  { value: "recommended", label: "Recommended" },
  { value: "not_required", label: "Not required" },
];
const CHECKIN_REQUIRED_FIELDS = ["Ticket No", "PNR", "Last Name", "First name"];

const emptyForm = {
  fareClassCode: "",
  fareClassName: "",
  airlineId: "",
  cabinClassId: "",
  flightScope: "",
  fareType: "",
  checkinStatus: "",
  checkinRequiredFields: [],
  checkinText: "",
  checkinUrl: "",
  bookingClassCodes: "",
  status: "active",
};

const AddFareClassModal = ({ open, onClose, onSave, fareClass, cabinClasses = [], apiError }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [airlineSearch, setAirlineSearch] = useState("");

  const { data: airlines = [] } = useGetAirlines({ page: 1, limit: 100, status: "", search: airlineSearch });

  // const airlineOptions = useMemo(() =>
  //   airlines.map((a) => ({ value: a.id, label: `${a.airlineName} (${a.iataCode})` })),
  //   [airlines]
  // );
  const airlineOptions = useMemo(() => {
    const mapped = airlines.map((a) => ({
      value: a.id,
      label: `${a.airlineName} (${a.iataCode})`,
    }));
    // inject selected airline if missing
    if (
      fareClass?.airlineId &&
      fareClass?.airline &&
      !mapped.some((a) => a.value === fareClass.airlineId)
    ) {
      mapped.unshift({
        value: fareClass.airlineId,
        label: `${fareClass.airline} (${fareClass.airlineCode})`,
      });
    }

    return mapped;
  }, [airlines, fareClass]);

  const handleAirlineSearch = useCallback((q) => setAirlineSearch(q.trim()), []);

  useEffect(() => {
    if (fareClass) {
      setForm({
        fareClassCode: fareClass.fareClassCode || "",
        fareClassName: fareClass.fareClassName || "",
        airlineId: fareClass.airlineId !== undefined && fareClass.airlineId !== null ? fareClass.airlineId : "",
        cabinClassId: fareClass.cabinClassId ?? "",
        flightScope: fareClass.flightScope || "",
        fareType: fareClass.fareType || "",
        checkinStatus: fareClass.checkinStatus || "",
        checkinRequiredFields: fareClass.checkinRequiredFields || [],
        checkinText: fareClass.checkinText || "",
        checkinUrl: fareClass.checkinUrl || "",
        // Stored as an array, edited as a comma separated box.
        bookingClassCodes: Array.isArray(fareClass.bookingClassCodes)
          ? fareClass.bookingClassCodes.join(", ")
          : fareClass.bookingClassCodes || "",
        status: fareClass.status || "active",
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [fareClass, open]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const handleBlur = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
    validateField(key, form[key]);
  };

  const validateField = (key, value) => {
    let error = "";
    switch (key) {
      case "fareClassCode":
        if (!value || !value.trim()) error = "Airline code is required";
        else if (!/^[A-Za-z0-9]{1,10}$/.test(value.trim())) error = "Code must be 1-10 alphanumeric characters";
        break;
      case "fareClassName":
        if (!value || !value.trim()) error = "Fare family name is required";
        else if (value.trim().length < 2) error = "Name must be at least 2 characters";
        break;
      case "airlineId":
        if (!value) error = "Airline is required";
        break;
      case "cabinClassId":
        if (!value) error = "Cabin class is required";
        break;
      case "flightScope":
        if (!value) error = "Flight scope is required";
        break;
      case "fareType":
        if (!value) error = "Fare type is required";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [key]: error }));
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    let isValid = true;

    if (!form.fareClassCode.trim()) { newErrors.fareClassCode = "Airline code is required"; isValid = false; }
    else if (!/^[A-Za-z0-9]{1,10}$/.test(form.fareClassCode.trim())) { newErrors.fareClassCode = "Code must be 1-10 alphanumeric characters"; isValid = false; }
    if (!form.fareClassName.trim()) { newErrors.fareClassName = "Fare family name is required"; isValid = false; }
    else if (form.fareClassName.trim().length < 2) { newErrors.fareClassName = "Name must be at least 2 characters"; isValid = false; }
    if (!form.airlineId) { newErrors.airlineId = "Airline is required"; isValid = false; }
    if (!form.cabinClassId) { newErrors.cabinClassId = "Cabin class is required"; isValid = false; }
    if (!form.flightScope) { newErrors.flightScope = "Flight scope is required"; isValid = false; }
    if (!form.fareType) { newErrors.fareType = "Fare type is required"; isValid = false; }

    setErrors(newErrors);
    return isValid;
  };

  const handleReset = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = () => {
    if (!validateAllFields()) return;

    const payload = {
      fareClassCode: form.fareClassCode.trim().toUpperCase(),
      fareClassName: form.fareClassName.trim(),
      airlineId: parseInt(form.airlineId, 10),
      cabinClassId: parseInt(form.cabinClassId, 10),
      flightScope: form.flightScope,
      fareType: form.fareType,
      checkinStatus: form.checkinStatus || null,
      checkinRequiredFields: form.checkinStatus ? form.checkinRequiredFields : [],
      checkinText: form.checkinStatus ? form.checkinText.trim() : "",
      checkinUrl: form.checkinStatus ? form.checkinUrl.trim() : "",
      bookingClassCodes: form.bookingClassCodes,
      status: form.status,
    };

    onSave(payload);
    handleClose();
  };

  const hasErrors = Object.values(errors).some((e) => e !== "");

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={fareClass ? "Edit Fare Family" : "Add New Fare Family"}
      width="780px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>Close</button>
          <button className={styles.saveBtn} onClick={handleSubmit}>Save Changes</button>
        </>
      }
    >
      <div className={styles.iconHeader}>
        <div className={styles.icon}>🎫</div>
        <div className={styles.headerText}>
          <h3>{fareClass ? "Edit Fare Family Details" : "Add New Fare Family"}</h3>
          <p>
            {fareClass
              ? "Update fare family configuration for flight identification"
              : "Create a new fare family linked to an airline and cabin class"}
          </p>
        </div>
      </div>

      {apiError && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Error:</strong> {apiError}
          </div>
        </div>
      )}

      {hasErrors && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(
                ([key, error]) => error && <li key={key}>{error}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Basic Information */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>BASIC INFORMATION</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>AIRLINE CODE <span className={styles.required}>*</span></label>
            <input
              value={form.fareClassCode}
              onChange={(e) => handleChange("fareClassCode", e.target.value.toUpperCase())}
              onBlur={() => handleBlur("fareClassCode")}
              placeholder="Y"
              maxLength={10}
              className={errors.fareClassCode ? styles.inputError : ""}
            />
            {errors.fareClassCode && <span className={styles.errorText}>{errors.fareClassCode}</span>}
            <span className={styles.helpText}>Up to 10 characters (e.g., Y, B, M, J, C, F, YOW)</span>
          </div>

          {/* Booking classes. An Airtuerk import states only the airline's
              booking class letter and never a fare name, so this is what lets an
              imported flight show this fare instead of a bare "W". */}
          <div className={styles.field}>
            <label>BOOKING CLASS CODES</label>
            <input
              value={form.bookingClassCodes}
              onChange={(e) => handleChange("bookingClassCodes", e.target.value.toUpperCase())}
              placeholder="W, Q, T"
              className={errors.bookingClassCodes ? styles.inputError : ""}
            />
            {errors.bookingClassCodes && (
              <span className={styles.errorText}>{errors.bookingClassCodes}</span>
            )}
            <span className={styles.helpText}>
              Letters the airline sells this fare in, separated by commas. Imported bookings carry
              only this letter, so it is how they resolve to this fare. Leave blank if unknown.
            </span>
          </div>

          <div className={styles.field}>
            <label>STATUS</label>
            <div className={styles.statusToggle}>
              <input
                type="checkbox"
                id="fareClassStatus"
                checked={form.status === "active"}
                onChange={(e) => handleChange("status", e.target.checked ? "active" : "inactive")}
                className={styles.toggleInput}
              />
              <label htmlFor="fareClassStatus" className={styles.toggleLabel}>
                <span className={styles.toggleSwitch}></span>
                <span className={styles.toggleText}>
                  {form.status === "active" ? "Active" : "Inactive"}
                </span>
              </label>
            </div>
            <span className={styles.helpText}>Only active fare families appear in bookings</span>
          </div>

          <div className={styles.fieldWide}>
            <label>FARE FAMILY NAME <span className={styles.required}>*</span></label>
            <input
              value={form.fareClassName}
              onChange={(e) => handleChange("fareClassName", e.target.value)}
              onBlur={() => handleBlur("fareClassName")}
              placeholder="e.g., Economy Full Flex, Business Saver, First Class Premium"
              className={errors.fareClassName ? styles.inputError : ""}
            />
            {errors.fareClassName && <span className={styles.errorText}>{errors.fareClassName}</span>}
            <span className={styles.helpText}>Descriptive name for this fare family</span>
          </div>
        </div>
      </div>

      {/* Relationships */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>RELATIONSHIPS</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>AIRLINE <span className={styles.required}>*</span></label>
            <SearchableSelect
              value={form.airlineId}
              onChange={(val) => handleChange("airlineId", val)}
              onBlur={() => handleBlur("airlineId")}
              onSearch={handleAirlineSearch}
              options={airlineOptions}
              placeholder="Select airline"
              searchPlaceholder="Search airlines..."
              error={!!errors.airlineId}
            />
            {errors.airlineId && <span className={styles.errorText}>{errors.airlineId}</span>}
          </div>

          <div className={styles.field}>
            <label>CABIN CLASS <span className={styles.required}>*</span></label>
            <select
              value={form.cabinClassId}
              onChange={(e) => handleChange("cabinClassId", e.target.value)}
              onBlur={() => handleBlur("cabinClassId")}
              className={errors.cabinClassId ? styles.inputError : ""}
            >
              <option value="">Select cabin class</option>
              {cabinClasses.map((cabin) => (
                <option key={cabin.id} value={cabin.id}>
                  {cabin.cabinClassName} ({cabin.cabinClassCode})
                </option>
              ))}
            </select>
            {errors.cabinClassId && <span className={styles.errorText}>{errors.cabinClassId}</span>}
          </div>
        </div>
      </div>

      {/* Flight Scope & Fare Type */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>FLIGHT SCOPE & FARE TYPE</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>FLIGHT SCOPE <span className={styles.required}>*</span></label>
            <select
              value={form.flightScope}
              onChange={(e) => handleChange("flightScope", e.target.value)}
              onBlur={() => handleBlur("flightScope")}
              className={errors.flightScope ? styles.inputError : ""}
            >
              <option value="">Select flight scope</option>
              {FLIGHT_SCOPES.map((scope) => (
                <option key={scope} value={scope}>{scope}</option>
              ))}
            </select>
            {errors.flightScope && <span className={styles.errorText}>{errors.flightScope}</span>}
            <span className={styles.helpText}>Domestic, International, Intercontinental, Touristic, Special, or All Flights</span>
          </div>

          <div className={styles.field}>
            <label>FARE TYPE <span className={styles.required}>*</span></label>
            <select
              value={form.fareType}
              onChange={(e) => handleChange("fareType", e.target.value)}
              onBlur={() => handleBlur("fareType")}
              className={errors.fareType ? styles.inputError : ""}
            >
              <option value="">Select fare type</option>
              {FARE_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {errors.fareType && <span className={styles.errorText}>{errors.fareType}</span>}
            <span className={styles.helpText}>Light, Standard, Flex, Premium, Touristic, Basic, or Plus</span>
          </div>
        </div>
      </div>

      {/* Online Check-in Configuration */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>ONLINE CHECK-IN CONFIGURATION</h4>
        <div className={styles.formGrid}>
          <div className={styles.fieldWide}>
            <label>CHECK-IN STATUS</label>
            <select
              value={form.checkinStatus}
              onChange={(e) => handleChange("checkinStatus", e.target.value)}
            >
              {CHECKIN_STATUSES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <span className={styles.helpText}>
              Fare-level overrides airline-level. Leave empty to inherit from airline or system default.
            </span>
          </div>

          {form.checkinStatus && (
            <>
              <div className={styles.fieldWide}>
                <label>REQUIRED FIELDS FOR CHECK-IN</label>
                <div className={styles.checkboxGroup}>
                  {CHECKIN_REQUIRED_FIELDS.map((field) => (
                    <label key={field} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={form.checkinRequiredFields.includes(field)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...form.checkinRequiredFields, field]
                            : form.checkinRequiredFields.filter((f) => f !== field);
                          handleChange("checkinRequiredFields", updated);
                        }}
                      />
                      <span>{field}</span>
                    </label>
                  ))}
                </div>
                <span className={styles.helpText}>Select which reference fields passengers need for online check-in</span>
              </div>

              <div className={styles.fieldWide}>
                <label>CUSTOM INSTRUCTION TEXT</label>
                <input
                  value={form.checkinText}
                  onChange={(e) => handleChange("checkinText", e.target.value)}
                  placeholder="Leave empty to auto-generate based on status and required fields"
                />
                <span className={styles.helpText}>Custom text overrides auto-generated instructions. Leave blank for default text.</span>
              </div>

              <div className={styles.fieldWide}>
                <label>CHECK-IN URL</label>
                <input
                  value={form.checkinUrl}
                  onChange={(e) => handleChange("checkinUrl", e.target.value)}
                  placeholder="e.g., https://www.airline.com/checkin"
                />
                <span className={styles.helpText}>Direct link to the airline's online check-in page</span>
              </div>
            </>
          )}
        </div>

        {/* Fallback Logic Info */}
        <div className={styles.businessNotice} style={{ marginTop: "12px" }}>
          <div className={styles.noticeIcon}>🔄</div>
          <div className={styles.noticeContent}>
            <strong>Configuration Priority (Fallback Logic)</strong>
            <ul>
              <li><strong>1st:</strong> Fare-level config (this form) — always takes priority</li>
              <li><strong>2nd:</strong> Airline-level config — used if fare has no config</li>
              <li><strong>3rd:</strong> System default — ensures voucher never shows empty info</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Business Rules */}
      <div className={styles.businessNotice}>
        <div className={styles.noticeIcon}>📋</div>
        <div className={styles.noticeContent}>
          <strong>Business Rules</strong>
          <ul>
            <li>Each fare family must be linked to exactly one airline and one cabin class</li>
            <li>Fare family name must be unique per airline (duplicates across airlines are allowed)</li>
            <li>Airline code can be shared across multiple fare families (no uniqueness restriction)</li>
            <li>Flight scope and fare type are required for every fare family</li>
            <li>Fare-level check-in config always overrides airline-level</li>
            <li>Flight voucher will never show empty check-in information (system default applies)</li>
          </ul>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddFareClassModal;
